import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp } from 'lucide-react';

/** 지연 없는 도움말. 스크롤 영역에 잘리지 않으며 키보드와 터치로도 연다. */
export default function HelpTooltip({ label, children }) {
  const id = useId();
  const anchor = useRef(null);
  const tip = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPosition(null); return undefined; }
    const a = anchor.current.getBoundingClientRect();
    const t = tip.current.getBoundingClientRect();
    const above = a.top - t.height >= 8;
    setPosition({
      left: Math.max(8, Math.min(a.left + (a.width - t.width) / 2, window.innerWidth - t.width - 8)),
      top: Math.max(8, Math.min(above ? a.top - t.height : a.bottom, window.innerHeight - t.height - 8)),
      above,
    });
    const close = () => setOpen(false);
    const outside = (e) => { if (!anchor.current?.contains(e.target) && !tip.current?.contains(e.target)) close(); };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    document.addEventListener('pointerdown', outside);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('pointerdown', outside);
    };
  }, [open]);
  return <span className="inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
    <button ref={anchor} type="button" aria-label={`${label} 도움말`} aria-describedby={open ? id : undefined}
      className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onClick={() => setOpen(true)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}>
      <CircleHelp className="size-3.5" aria-hidden="true" />
    </button>
    {open && createPortal(<span ref={tip} id={id} role="tooltip" className="fixed w-max max-w-[calc(100vw-1rem)] text-xs"
      style={{ zIndex: 9999985, left: position?.left || 0, top: position?.top || 0, visibility: position ? 'visible' : 'hidden', paddingTop: position?.above === false ? 6 : 0, paddingBottom: position?.above ? 6 : 0 }}>
      <span className="block max-w-xs whitespace-normal break-words rounded-lg bg-gray-900 px-3 py-2 leading-relaxed text-white shadow-lg">{children}</span>
    </span>, document.body)}
  </span>;
}
